import React, { useState, useEffect } from 'react';
import { useTranslation } from '../contexts/LanguageContext';
import '../styles/GameOverModal.css';

const GameOverModal = ({ 
  isOpen, 
  winner, 
  reason, 
  onClose, 
  playerColor, 
  message,
  isMultiplayer = false,
  onRematchRequest,
  onRematchDecline,
  rematchState = null, // null, 'waiting', 'opponent_requested', 'accepted', 'declined'
  opponentName = 'Opponent'
}) => {
  const { t } = useTranslation();
  const [hasRequestedRematch, setHasRequestedRematch] = useState(false);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setHasRequestedRematch(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Determine if current player won or lost
  const isCurrentPlayerWinner = playerColor && winner && playerColor === winner;
  const isCurrentPlayerLoser = playerColor && winner && playerColor !== winner;

  const getMessage = () => {
    if (reason === 'resignation') {
      const resigningPlayer = winner === 'white' ? t('game.black', 'Black') : t('game.white', 'White');
      return `${resigningPlayer} ${t('game.resigns', 'Resigns')}`;
    } else if (reason === 'forfeit') {
      const forfeitingPlayer = winner === 'white' ? t('game.black', 'Black') : t('game.white', 'White');
      return `${forfeitingPlayer} ${t('game.forfeits', 'Forfeits')}`;
    } else if (reason === 'checkmate') {
      return t('game.checkmate');
    } else if (reason === 'stalemate') {
      return `${t('game.stalemate')} - ${t('game.draw')}`;
    } else if (reason === 'king_captured') {
      return t('game.kingCaptured', 'King Captured!');
    }
    return t('game.gameOver');
  };

  const getIcon = () => {
    if (reason === 'stalemate') {
      return '🤝';
    }
    if (reason === 'forfeit' || reason === 'resignation') {
      return isCurrentPlayerWinner ? '🏆' : '🏳️';
    }
    if (isCurrentPlayerWinner) {
      return '🏆';
    } else if (isCurrentPlayerLoser) {
      return '😔';
    }
    return '🎮';
  };

  const getResultText = () => {
    if (reason === 'stalemate') {
      return t('game.drawGame');
    }
    if (isCurrentPlayerWinner) {
      return `🎉 ${t('game.youWin')} 🎉`;
    } else if (isCurrentPlayerLoser) {
      return t('game.youLose');
    }
    // Fallback if playerColor not available
    if (winner) {
      return winner === 'white' ? t('game.whiteWins') : t('game.blackWins');
    }
    return '';
  };

  const getWinnerAnnouncement = () => {
    if (!winner || reason === 'stalemate') return null;
    const winnerName = winner === 'white' ? `⚪ ${t('game.white', 'White')}` : `⚫ ${t('game.black', 'Black')}`;
    return `${t('game.winner', 'Winner')}: ${winnerName}`;
  };

  const handleRematchRequest = () => {
    setHasRequestedRematch(true);
    if (onRematchRequest) {
      onRematchRequest();
    }
  };

  const handleDecline = () => {
    if (onRematchDecline) {
      onRematchDecline();
    }
    onClose();
  };

  const getRematchStatusMessage = () => {
    if (rematchState === 'waiting') {
      return `⏳ ${t('game.waitingForOpponent', 'Waiting for opponent...')}`;
    }
    if (rematchState === 'opponent_requested') {
      return `🔔 ${opponentName} ${t('game.wantsRematch', 'wants a rematch!')}`;
    }
    if (rematchState === 'accepted') {
      return `✅ ${t('game.rematchAccepted', 'Rematch accepted! Starting new game...')}`;
    }
    if (rematchState === 'declined') {
      return `❌ ${t('game.rematchDeclined', 'Rematch declined. Returning to lobby...')}`;
    }
    return null;
  };

  const showRematchButtons = isMultiplayer && rematchState !== 'accepted' && rematchState !== 'declined';

  return (
    <div className="game-over-overlay" onClick={handleDecline} data-testid="game-over-overlay">
      <div className="game-over-modal" onClick={(e) => e.stopPropagation()}>
        <div className="game-over-icon">{getIcon()}</div>
        <h2>{getMessage()}</h2>
        {winner && (
          <>
            <p className="winner-announcement">{getWinnerAnnouncement()}</p>
            <p className={`winner-declaration ${isCurrentPlayerWinner ? 'you-win' : ''} ${isCurrentPlayerLoser ? 'you-lose' : ''}`}>
              {getResultText()}
            </p>
          </>
        )}
        
        {/* Rematch Section for Multiplayer */}
        {showRematchButtons && (
          <div className="rematch-section">
            {rematchState && (
              <p className="rematch-status">{getRematchStatusMessage()}</p>
            )}
            
            <div className="rematch-buttons">
              {!hasRequestedRematch && rematchState !== 'opponent_requested' && (
                <>
                  <button 
                    className="rematch-btn accept"
                    onClick={handleRematchRequest}
                    data-testid="rematch-btn"
                  >
                    🔄 {t('game.rematch', 'Rematch')}
                  </button>
                  <button 
                    className="rematch-btn decline"
                    onClick={handleDecline}
                    data-testid="decline-btn"
                  >
                    ❌ {t('game.decline', 'Decline')}
                  </button>
                </>
              )}
              
              {hasRequestedRematch && rematchState === 'waiting' && (
                <button 
                  className="rematch-btn waiting"
                  disabled
                >
                  ⏳ {t('game.waiting', 'Waiting...')}
                </button>
              )}
              
              {rematchState === 'opponent_requested' && !hasRequestedRematch && (
                <>
                  <button 
                    className="rematch-btn accept"
                    onClick={handleRematchRequest}
                    data-testid="accept-rematch-btn"
                  >
                    ✅ {t('game.acceptRematch', 'Accept Rematch')}
                  </button>
                  <button 
                    className="rematch-btn decline"
                    onClick={handleDecline}
                    data-testid="decline-rematch-btn"
                  >
                    ❌ {t('game.decline', 'Decline')}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Loading state for accepted rematch */}
        {rematchState === 'accepted' && (
          <div className="rematch-loading">
            <div className="spinner"></div>
            <p>{t('game.startingNewGame', 'Starting new game...')}</p>
          </div>
        )}

        {/* Only show Continue button for solo games or when rematch is not available */}
        {!isMultiplayer && (
          <button className="game-over-button" onClick={onClose} data-testid="continue-btn">
            {t('game.continue', 'Continue')}
          </button>
        )}
      </div>
    </div>
  );
};

export default GameOverModal;
